import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Phone, Shield, RefreshCw, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { API, getApiUrl } from '../utils/api';

const OTPVerificationModal = ({ 
  isOpen, 
  onClose, 
  phoneNumber, 
  deviceToken,
  deviceType,
  deviceName,
  onVerified,
  mode = 'verification' // 'registration' or 'verification'
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState('sending'); // sending, input, verifying, success, error
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [expiresAt, setExpiresAt] = useState(null);
  const [debugCode, setDebugCode] = useState(null);
  const inputRefs = useRef([]);

  // Send OTP on modal open
  useEffect(() => {
    if (isOpen && phoneNumber) {
      sendOTP();
    }
  }, [isOpen, phoneNumber]);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Expiry countdown
  useEffect(() => {
    if (expiresAt) {
      const interval = setInterval(() => {
        const remaining = Math.max(0, Math.floor((new Date(expiresAt) - new Date()) / 1000));
        if (remaining <= 0) {
          setError(t('auth.codeExpired', 'Code expired. Please request a new code.'));
          clearInterval(interval);
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [expiresAt, t]);

  const sendOTP = async () => {
    setStep('sending');
    setError(null);
    setDebugCode(null);
    
    try {
      const response = await fetch(`${getApiUrl()}/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: phoneNumber })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStep('input');
        setCountdown(60); // 60 seconds before allowing resend
        setExpiresAt(new Date(Date.now() + data.expires_in_minutes * 60 * 1000));
        
        // Debug mode - show code in UI
        if (data.debug_code) {
          setDebugCode(data.debug_code);
        }
        
        // Focus first input
        setTimeout(() => inputRefs.current[0]?.focus(), 100);
      } else {
        setError(data.detail || t('auth.errorSendingCode', 'Error sending code'));
        setStep('error');
      }
    } catch (err) {
      console.error('Send OTP error:', err);
      setError(t('auth.connectionError', 'Connection error. Please try again.'));
      setStep('error');
    }
  };

  const verifyOTP = async (codeOverride = null) => {
    const code = codeOverride || otp.join('');
    if (code.length !== 6) {
      setError(t('auth.enterFullCode', 'Please enter full 6-digit code'));
      return;
    }

    setStep('verifying');
    setError(null);

    try {
      const response = await fetch(`${getApiUrl()}/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone_number: phoneNumber,
          code: code,
          device_token: deviceToken,
          device_type: deviceType,
          device_name: deviceName
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStep('success');
        setTimeout(() => {
          if (onVerified) onVerified(data);
        }, 1500);
      } else {
        setError(data.detail || 'Ungültiger Code');
        setStep('input');
        // Clear OTP inputs
        setOtp(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      console.error('Verify OTP error:', err);
      setError(t('auth.connectionError', 'Connection error. Please try again.'));
      setStep('input');
    }
  };

  const handleOtpChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are entered
    if (newOtp.every(d => d !== '')) {
      // Small delay to let the UI update, then auto-verify
      setTimeout(() => {
        verifyOTP(newOtp.join(''));
      }, 150);
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    // Handle paste
    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then(text => {
        const digits = text.replace(/\D/g, '').slice(0, 6).split('');
        const newOtp = [...otp];
        digits.forEach((d, i) => {
          if (i < 6) newOtp[i] = d;
        });
        setOtp(newOtp);
        if (digits.length === 6) {
          setTimeout(() => verifyOTP(newOtp.join('')), 150);
        }
      });
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text');
    const digits = text.replace(/\D/g, '').slice(0, 6).split('');
    const newOtp = [...otp];
    digits.forEach((d, i) => {
      if (i < 6) newOtp[i] = d;
    });
    setOtp(newOtp);
    if (digits.length === 6) {
      setTimeout(() => verifyOTP(newOtp.join('')), 150);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-4 sm:p-6 text-white text-center">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
            <Shield className="w-6 h-6 sm:w-8 sm:h-8" />
          </div>
          <h2 className="text-lg sm:text-xl font-bold">
            {mode === 'registration' ? t('auth.verifyPhone', 'Verify phone') : t('auth.confirmDevice', 'Confirm device')}
          </h2>
          <p className="text-emerald-100 text-xs sm:text-sm mt-1">
            {mode === 'registration' 
              ? t('auth.confirmYourPhone', 'Confirm your phone number')
              : t('auth.newDeviceDetected', 'New device detected - Please confirm')}
          </p>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          {/* Phone number display */}
          <div className="flex items-center justify-center gap-2 mb-4 sm:mb-6 p-3 bg-gray-50 rounded-lg">
            <Phone className="w-5 h-5 text-gray-500 flex-shrink-0" />
            <span className="font-medium">{phoneNumber}</span>
          </div>

          {/* Sending state */}
          {step === 'sending' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
              <p className="text-gray-600">{t('auth.sendingCode', 'Sending code...')}</p>
            </div>
          )}

          {/* OTP Input */}
          {step === 'input' && (
            <>
              <p className="text-center text-gray-600 mb-4">
                {t('auth.enterSmsCode', 'Enter the 6-digit code we sent via SMS:')}
              </p>

              {/* Debug code display */}
              {debugCode && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                  <p className="text-xs text-yellow-700">🔐 Debug - Code:</p>
                  <p className="font-mono text-2xl font-bold text-yellow-800">{debugCode}</p>
                </div>
              )}

              {/* OTP inputs */}
              <div className="flex justify-center gap-1 sm:gap-2 mb-4 sm:mb-6">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={el => inputRefs.current[index] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    className="w-10 h-12 sm:w-12 sm:h-14 text-center text-xl sm:text-2xl font-bold border-2 rounded-lg 
                             focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 
                             outline-none transition-all"
                  />
                ))}
              </div>

              {/* Error message */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {/* Verify button */}
              <Button
                onClick={() => verifyOTP()}
                disabled={otp.some(d => d === '')}
                className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-lg"
              >
                {t('auth.confirm', 'Confirm')}
              </Button>

              {/* Resend button */}
              <div className="mt-4 text-center">
                {countdown > 0 ? (
                  <p className="text-sm text-gray-500">
                    {t('auth.requestNewCodeIn', 'Request new code in {{seconds}}s').replace('{{seconds}}', countdown)}
                  </p>
                ) : (
                  <button
                    onClick={sendOTP}
                    className="text-sm text-emerald-600 hover:text-emerald-700 flex items-center justify-center gap-1 mx-auto"
                  >
                    <RefreshCw className="w-4 h-4" />
                    {t('auth.sendNewCode', 'Send new code')}
                  </button>
                )}
              </div>
            </>
          )}

          {/* Verifying state */}
          {step === 'verifying' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
              <p className="text-gray-600">{t('auth.verifyingCode', 'Verifying code...')}</p>
            </div>
          )}

          {/* Success state */}
          {step === 'success' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">{t('auth.successVerified', 'Successfully verified!')}</h3>
              <p className="text-gray-600">
                {mode === 'registration' 
                  ? t('auth.phoneConfirmed', 'Your phone number has been confirmed.')
                  : t('auth.deviceConfirmed', 'This device has been successfully confirmed.')}
              </p>
            </div>
          )}

          {/* Error state */}
          {step === 'error' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-10 h-10 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">{t('common.error', 'Error')}</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button onClick={sendOTP} variant="outline">
                {t('common.tryAgain', 'Try again')}
              </Button>
            </div>
          )}
        </div>

        {/* Close button (only in input/error state) */}
        {(step === 'input' || step === 'error') && (
          <button
            onClick={onClose}
            className="absolute top-2 right-2 sm:top-4 sm:right-4 p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-full transition-colors z-10"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default OTPVerificationModal;
